import _toConsumableArray from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/toConsumableArray.js";
import { ChallengeCard, ChannelCard, LoadingChallengeCard } from '@infl-ts/ui-advocate-components';
import { BlockWidthContext, BlockWidthWrapper, useContainerMediaQuery } from '@infl-ts/util-block-width-context';
import { useEmbedMode } from '@infl-ts/util-embed-mode-provider';
import { S$ } from '@infl-ts/util-fp-ts';
import { memoize } from '@infl-ts/util-function';
import { logQueryError } from '@infl-ts/util-query-errors';
import { addValidProtocol } from '@infl-ts/util-url';
import { createChallengePlayerLink, useRefetchOnChallengeClose } from '@infl-ts/util-use-challenge-player';
import { useDeviceType } from '@infl-ts/util-use-device-type';
import { Box, Grid } from '@mui/material';
import * as DE from '@nll/datum/lib/DatumEither';
import * as A from 'fp-ts/Array';
import { constant, constNull, flow, pipe } from 'fp-ts/function';
import * as Num from 'fp-ts/number';
import * as O from 'fp-ts/Option';
import * as Ord from 'fp-ts/Ord';
import * as S from 'fp-ts/struct';
import { useContext } from 'react';
import { useLocation } from 'react-router';
import ChallengeCarousel, { CarouselWrapper } from './challenge-carousel';
import { ChallengeCarouselSortBy } from './codecs';
import { useCarouselChallengesQuery, useChallengeIdsQuery } from './queries';
import ScrollableChallenges from './scrollable-challenges';
import { jsx as _jsx } from "react/jsx-runtime";
var clampNum = Ord.clamp(Num.Ord);
export var cardsToShow = memoize(function (containerWidth) {
  var minCardWidth = 230;
  var cardPadding = 22;
  if (containerWidth === 0) return 0;
  return clampNum(1, Infinity)(Math.floor(containerWidth / (minCardWidth + cardPadding)));
});
export var getTitle = function getTitle(title, challengeSort) {
  if (title !== null) return title;
  return challengeSort === ChallengeCarouselSortBy.map.RECOMMENDED ? 'Recommended for you' : "What's New";
};
export var cardGridColumns = function cardGridColumns(maxCards) {
  if (maxCards >= 4) return 3;
  if (maxCards === 3) return 4;
  if (maxCards === 2) return 6;
  return 12;
};

var CardContainer = function CardContainer(_ref) {
  var children = _ref.children,
      numberOfCardsToShow = _ref.numberOfCardsToShow;
  return /*#__PURE__*/_jsx(Grid, {
    item: true,
    xs: cardGridColumns(numberOfCardsToShow),
    "data-element": "Grid",
    "data-component": "CardContainer",
    "data-source-file": "index.tsx",
    children: children
  });
};

var ChallengesContainer = function ChallengesContainer(_ref2) {
  var children = _ref2.children;
  return /*#__PURE__*/_jsx(Grid, {
    container: true,
    spacing: 2.5,
    sx: {
      pb: 0.25,
      px: 0.25
    },
    "data-element": "Grid",
    "data-component": "ChallengesContainer",
    "data-source-file": "index.tsx",
    children: children
  });
};

var CarouselCard = function CarouselCard(_ref3) {
  var item = _ref3.item,
      pathname = _ref3.pathname;

  var _useEmbedMode = useEmbedMode(),
      host = _useEmbedMode.host,
      isEmbedded = _useEmbedMode.isEmbedded;

  var isEmbeddedOnExternalSite = isEmbedded && !!host;
  return item.__typename === 'Challenge' ? /*#__PURE__*/_jsx(ChallengeCard, {
    challenge: item,
    challengeLink: createChallengePlayerLink(pathname, {
      uuid: item.id
    })
  }) : /*#__PURE__*/_jsx(ChannelCard, {
    channel: item,
    openInNewTab: isEmbeddedOnExternalSite,
    linkTo: {
      pathname: isEmbeddedOnExternalSite ? "".concat(addValidProtocol(host), "/channels/").concat(item.id) : "/channels/".concat(item.id),
      state: {
        prevUrl: pathname
      }
    }
  });
};

var CarouselChallenges = function CarouselChallenges(_ref4) {
  var challenges = _ref4.challenges,
      title = _ref4.title,
      challengeSort = _ref4.challengeSort,
      onMobileOrTabletDevice = _ref4.onMobileOrTabletDevice;

  var _useLocation = useLocation(),
      pathname = _useLocation.pathname;

  var scrollable = useContainerMediaQuery({
    maxWidth: 'md',
    minWidth: 1
  });
  var containerWidth = useContext(BlockWidthContext);
  var carouselTitle = getTitle(title, challengeSort);
  useRefetchOnChallengeClose([useCarouselChallengesQuery.document]);
  return scrollable && onMobileOrTabletDevice ? /*#__PURE__*/_jsx(ScrollableChallenges, {
    title: carouselTitle,
    children: challenges.map(function (c) {
      return /*#__PURE__*/_jsx(Box, {
        sx: {
          width: 230
        },
        children: /*#__PURE__*/_jsx(CarouselCard, {
          item: c,
          pathname: pathname
        }, c.id)
      }, c.id);
    })
  }) : pipe(cardsToShow(containerWidth), function (numberOfCardsToShow) {
    return numberOfCardsToShow ? /*#__PURE__*/_jsx(ChallengeCarousel, {
      showIndicators: challenges.length > numberOfCardsToShow,
      title: carouselTitle,
      children: pipe(challenges, A.chunksOf(numberOfCardsToShow), A.mapWithIndex(function (i, challengeGroup) {
        return /*#__PURE__*/_jsx(ChallengesContainer, {
          children: challengeGroup.map(function (c) {
            return /*#__PURE__*/_jsx(CardContainer, {
              numberOfCardsToShow: numberOfCardsToShow,
              children: /*#__PURE__*/_jsx(CarouselCard, {
                item: c,
                pathname: pathname
              })
            }, c.id);
          })
        }, i);
      }))
    }) : null;
  });
};

var LoadingChallengeCarousel = function LoadingChallengeCarousel(_ref5) {
  var title = _ref5.title,
      challengeSort = _ref5.challengeSort;
  var scrollable = useContainerMediaQuery({
    maxWidth: 'md'
  });
  var carouselTitle = getTitle(title, challengeSort);
  return pipe(cardsToShow(useContext(BlockWidthContext)), function (numberOfCardsToShow) {
    return A.makeBy(numberOfCardsToShow, function (i) {
      return /*#__PURE__*/_jsx(CardContainer, {
        numberOfCardsToShow: numberOfCardsToShow,
        children: /*#__PURE__*/_jsx(LoadingChallengeCard, {})
      }, i);
    });
  }, function (cards) {
    return scrollable ? /*#__PURE__*/_jsx(ScrollableChallenges, {
      title: carouselTitle,
      children: cards
    }) : /*#__PURE__*/_jsx(CarouselWrapper, {
      title: carouselTitle,
      children: /*#__PURE__*/_jsx(ChallengesContainer, {
        children: cards
      })
    });
  });
};

var getNonNullableNodes = function getNonNullableNodes(challengeArray) {
  return pipe(challengeArray === null || challengeArray === void 0 ? void 0 : challengeArray.edges, O.fromNullable, O.match(constant([]), A.filterMap(function (c) {
    return O.fromNullable(c === null || c === void 0 ? void 0 : c.node);
  })));
};

export var ChallengeCarouselBlock = function ChallengeCarouselBlock(_ref6) {
  var data = _ref6.data,
      title = _ref6.title,
      onlyChannels = _ref6.onlyChannels,
      onlyChallenges = _ref6.onlyChallenges,
      numberOfChannels = _ref6.numberOfChannels,
      numberOfChallenges = _ref6.numberOfChallenges,
      challengeSort = _ref6.challengeSort,
      onMobileOrTabletDevice = _ref6.onMobileOrTabletDevice;
  return pipe(data, DE.squash(function () {
    return /*#__PURE__*/_jsx(BlockWidthWrapper, {
      children: /*#__PURE__*/_jsx(LoadingChallengeCarousel, {
        title: title,
        challengeSort: challengeSort
      })
    });
  }, flow(logQueryError, constNull), flow(S.evolve({
    challenges: onlyChannels ? constant([]) : getNonNullableNodes,
    channels: onlyChallenges ? constant([]) : getNonNullableNodes
  }), S.evolve({
    challenges: function challenges(_challenges) {
      return A.takeLeft(numberOfChallenges)(_challenges);
    },
    channels: function channels(_channels) {
      return A.takeLeft(numberOfChannels)(_channels);
    }
  }), function (_ref7) {
    var challenges = _ref7.challenges,
        channels = _ref7.channels;
    return [].concat(_toConsumableArray(channels), _toConsumableArray(challenges));
  }, A.takeLeft(12), A.match(constNull, function (challenges) {
    return /*#__PURE__*/_jsx(BlockWidthWrapper, {
      children: /*#__PURE__*/_jsx(CarouselChallenges, {
        onMobileOrTabletDevice: onMobileOrTabletDevice,
        challenges: challenges,
        title: title,
        challengeSort: challengeSort
      })
    });
  }))));
};

var validNumberOfChallenges = function validNumberOfChallenges(numberOfChallenges) {
  return pipe(numberOfChallenges !== null && numberOfChallenges !== void 0 ? numberOfChallenges : 12, clampNum(0, 12));
};

var useProps = function useProps(_ref8) {
  var _ref8$block = _ref8.block,
      categories = _ref8$block.categories,
      title = _ref8$block.title,
      numberOfChannels = _ref8$block.numberOfChannels,
      challengeSort = _ref8$block.challengeSort,
      includeChannelContent = _ref8$block.includeChannelContent,
      numberOfChallenges = _ref8$block.numberOfChallenges,
      filterByChannelId = _ref8$block.filterByChannelId,
      filterByChallengeIds = _ref8$block.filterByChallengeIds;
  var deviceType = useDeviceType();
  var onMobileOrTabletDevice = deviceType === 'mobile' || deviceType === 'tablet';
  var numChallenges = validNumberOfChallenges(numberOfChallenges);
  var specificChallengeList = pipe(useChallengeIdsQuery({
    variables: {
      challengeIds: filterByChallengeIds !== null && filterByChallengeIds !== void 0 ? filterByChallengeIds : [],
      challengeSort: challengeSort,
      numChannels: numberOfChannels > 0 ? numberOfChannels : 1,
      categoryIds: categories ? categories : undefined
    },
    skip: !filterByChallengeIds
  }), DE.map(S$.partialEvolve({
    challenges: flow(O.fromNullable, O.map(A.map(function (challenge) {
      return {
        node: challenge
      };
    })), O.getOrElseW(constNull), function (nodes) {
      return {
        edges: nodes
      };
    })
  })));
  var challengeList = useCarouselChallengesQuery({
    variables: {
      channelId: filterByChannelId ? filterByChannelId : undefined,
      categoryIds: categories ? categories : undefined,
      numChallenges: numChallenges > 0 ? numChallenges : 1,
      numChannels: numberOfChannels > 0 ? numberOfChannels : 1,
      challengeSort: challengeSort,
      includeChannelContent: !!includeChannelContent
    },
    skip: !!filterByChallengeIds
  });
  return {
    data: filterByChallengeIds ? specificChallengeList : challengeList,
    title: title,
    onlyChannels: numChallenges < 1,
    onlyChallenges: numberOfChannels < 1,
    numberOfChannels: numberOfChannels,
    numberOfChallenges: numChallenges,
    challengeSort: challengeSort,
    onMobileOrTabletDevice: onMobileOrTabletDevice
  };
};

export default Object.assign(flow(useProps, ChallengeCarouselBlock), {
  displayName: 'ChallengeCarouselBlock'
});